// CheckinReport.js
import React, { useState, useCallback, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { Container, Form, Button, Row, Col } from 'react-bootstrap';
import AdminDataTable from './AdminDataTable';
import Swal from 'sweetalert2';
import { FaClipboardCheck, FaFilePdf, FaSearch } from 'react-icons/fa';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Navigation from './Navigation';
import Modal from 'react-bootstrap/Modal';
import AdminPageHeader from './AdminPageHeader';
import { formatReportDateTime } from './reportDateTime';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function CheckinReport() {
  const navigate = useNavigate();
  const token = localStorage.getItem('sessionToken');

  const [loading, setLoading] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [outletID, setOutletID] = useState('');
  const [interval, setInterval] = useState('day');

  const [reports, setReports] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [showDetail, setShowDetail] = useState(false);
  const [detailData, setDetailData] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');


const fetchDetail = async (date) => {
  setSelectedDate(date);
  setShowDetail(true);
  setDetailLoading(true);

  try {
    const res = await fetch(
      `${API_BASE_URL}admin/getcheckininterval?outlet_id=${outletID}&interval=${interval}&date=${date}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    const data = await res.json();

    if (data.error === "0") {
      setDetailData(data.data);
    } else {
      setDetailData([]);
      Swal.fire('Error', data.message || 'Gagal ambil detail', 'error');
    }

  } catch (err) {
    Swal.fire('Error', 'Gagal ambil detail check-in', 'error');
  } finally {
    setDetailLoading(false);
  }
};

  // ==============================
  // FETCH OUTLET LIST
  // ==============================
  const fetchOutlets = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}outletlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ limit: 100, offset: 0 })
      });

      const data = await response.json();

      if (data.error === "0") {
        setOutlets(data.results);
      } else {
        Swal.fire('Error', 'Gagal mengambil daftar outlet', 'error');
      }

    } catch (error) {
      console.error('Error fetching outlets:', error);
    }
  };

  useEffect(() => {
    fetchOutlets();
  }, []);

  // ==============================
  // FETCH SUMMARY REPORT
  // ==============================
  const fetchReport = useCallback(async () => {
    if (!token) {
      navigate('/loginadmin');
      return;
    }

    if (!outletID || !interval) {
      Swal.fire('Error', 'Outlet dan interval wajib diisi', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}reportstatistikcheckin?outlet_id=${outletID}&interval=${interval}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = await response.json();

      if (data.error === "0" && Array.isArray(data.data)) {
        setReports(data.data);
        setFiltered(data.data);
      } else {
        setReports([]);
        setFiltered([]);
        Swal.fire('Error', data.message || 'Gagal ambil data report', 'error');
      }

    } catch {
      Swal.fire('Error', 'Gagal ambil data report', 'error');
    } finally {
      setLoading(false);
    }
  }, [token, outletID, interval, navigate]);

  // ==============================
  // COLUMNS
  // ==============================
  const columns = [
    { name: 'Label', selector: row => row.label || row.date, sortable: true },
    { name: 'Total Check-in', selector: row => row.total || row.count, sortable: true },
    {
    name: 'Aksi',
    cell: row => (
      <Button
        size="sm"
        variant="info"
        onClick={() => fetchDetail(row.label)}
      >
        Detail
      </Button>
    )
  }
  ];
  const detailColumns = [
    {
      name: 'Member',
      selector: row => row.MemberName,
      cell: row => row.MemberName || '-',
    },
    {
      name: 'Check-in',
      selector: row => row.CheckInDate,
      cell: row => formatReportDateTime(row.CheckInDate),
    },
    {
      name: 'Check-out',
      selector: row => row.CheckOutDate?.Time || '',
      cell: row =>
        row.CheckOutDate?.Valid
          ? formatReportDateTime(row.CheckOutDate.Time)
          : '-',
    },
    {
      name: 'Source',
      selector: row => row.CheckinFrom,
      cell: row => formatReportDateTime(row.CheckinFrom),
    },
  ];

  // ==============================
  // EXPORT PDF
  // ==============================
  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text('Check-in Summary Report', 14, 10);

    const tableData = filtered.map(r => [
      r.label || r.date,
      r.total || r.count
    ]);

    autoTable(doc, {
      head: [['Label', 'Total']],
      body: tableData
    });

    doc.save('checkin-summary.pdf');
  };

  return (
    <>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaClipboardCheck}
          eyebrow="Operational reporting"
          title="Check-in Summary Report"
          description="Tinjau statistik check-in berdasarkan outlet dan periode untuk mendukung evaluasi operasional yang lebih akurat."
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: 'Check-in Summary Report' },
          ]}
          actions={(
            <Button variant="danger" onClick={exportPDF}>
              <FaFilePdf /> Export PDF
            </Button>
          )}
          meta={(
            <span className="soft-stat">
              {filtered.length} records
            </span>
          )}
        />

        <div className="section-card">
        <Row className="mb-3">
          <Col md={3}>
            <Form.Select
              value={outletID}
              onChange={(e) => setOutletID(e.target.value)}
            >
              <option value="">Select outlet</option>
              {outlets.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.outlet_name}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={3}>
            <Form.Select value={interval} onChange={e => setInterval(e.target.value)}>
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
              <option value="year">Yearly</option>
            </Form.Select>
          </Col>

          <Col md={3}>
            <Button className="w-100" onClick={fetchReport}>
              <FaSearch /> Show Report
            </Button>
          </Col>

          <Col md={3}>
            <Button variant="secondary" className="w-100" onClick={() => {
              setOutletID('');
              setInterval('day');
              setFiltered([]);
            }}>
              Reset Filter
            </Button>
          </Col>
        </Row>

        <AdminDataTable
          columns={columns}
          data={filtered}
          pagination
          progressPending={loading}
          striped
          highlightOnHover
          emptyTitle="No check-in report data found"
          emptyDescription="Pilih outlet dan periode laporan untuk menampilkan ringkasan check-in."
        />
        </div>
        <Modal
          show={showDetail}
          onHide={() => setShowDetail(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Detail Check-in ({selectedDate})
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <AdminDataTable
              columns={detailColumns}
              data={detailData}
              progressPending={detailLoading}
              pagination
              striped
              highlightOnHover
            />
          </Modal.Body>
        </Modal>


      </Container>
    </>
  );
}

export default CheckinReport;
